import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import ApplicationLayoutBodyBanner from '@rio-cloud/rio-uikit/ApplicationLayoutBodyBanner';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import LoadMoreButton from '@rio-cloud/rio-uikit/LoadMoreButton';
import SortDirection, { type SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import useLocalStorage from '@rio-cloud/rio-uikit/useLocalStorage';
import intersection from 'lodash/intersection';
import { type Identifier, orderBy } from 'natural-orderby';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getUserAccount } from '../../../../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { fetchAssets, fetchDevices, updateAssetTags } from '../../actions/assets/Assets.actions';
import { createAssetTags, fetchTags } from '../../actions/tags/Tags.actions';
import AssetsListDetailContainer from '../../containers/assets/details/AssetsListDetail.container';
import {
    assetTableSortChanges,
    getAssetTableSortColumn,
    getAssetTableSortDir,
    getAssetTagLastSuccessfullyAdded,
    getAssetTagLastSuccessfullyRemoved,
    getAssetTagsLastFailedAdded,
    getAssetTagsLastFailedRemoved,
    getAssetUpdateErrorCode,
    getAssetUpdateFailed,
    getAssetUpdateSuccessful,
    getAssets,
    getAssetsTableViewType,
    getAssociatedDevices,
    getFetching,
    getSearchString,
    getShowActiveAssets,
    getShowAssetsWithDataSource,
    getShowAssetsWithoutDataSource,
    getShowArchivedAssets,
    getTableCompactRows,
    getTableGridLines,
    openEasyOnboardingDialog,
    resetAssetTagsUpdatingState,
    resetAssetUpdateState,
    setSelectedAssetId,
} from '../../reducers/assets/assetsSlice';
import { getTags } from '../../reducers/tags/tagsSlice';
import FormattedMessageWithFallback from '../../utils/FormattedMessageWithFallback';
import { ReleaseInfoBannerContent } from '../ReleaseInfoBannerContent';
import { AssetsList } from './AssetsList';
import { AssetsListToolbar } from './AssetsListToolbar';
import { BulkArchiveAssetConfirmationDialog } from './BulkArchiveAssetConfirmationDialog';
import { closableSuccessNotification, nonVanishingErrorNotification } from './ClickableNotifications';
import { formatLicensePlateForDisplay } from './CsvExport.helper';
import { deviceTypeToDisplayedString } from './TelematicsName';
import {
    ASSETS_PER_PAGE,
    getAssetTableColumnsStorageKey,
    isAssetTableColumnSortable,
    isDeviceSharingsFeatureEnabled,
    normalizeAssetTableColumns,
} from './assetsUtils';
import { TagManagerWrapper } from './details/TagManagerWrapper';
import { type DeviceType, type SelectedTag, SelectedTagStatus } from './details/types';
import {
    type Asset,
    AssetStatus,
    type AssetType,
    type AssociatedDevice,
    type Tag,
    type TableColumnDetails,
} from './types';

const IS_FEATURE_BANNER_ENABLED = false;

export const Assets = () => {
    // Redux hooks
    const dispatch = useAppDispatch();

    const selectedAssetId = useAppSelector(state => state.assets.selectedAssetId);
    const tableViewType = useAppSelector(getAssetsTableViewType);
    const assets = useAppSelector(getAssets);
    const devices = useAppSelector(getAssociatedDevices);
    const fetching = useAppSelector(getFetching);
    const availableTags = useAppSelector(getTags);
    const accountId = useAppSelector(getUserAccount) ?? null;
    const searchString = useAppSelector(getSearchString);
    const showAssetsWithDataSource = useAppSelector(getShowAssetsWithDataSource);
    const showAssetsWithoutDataSource = useAppSelector(getShowAssetsWithoutDataSource);
    const showActiveAssets = useAppSelector(getShowActiveAssets);
    const showArchivedAssets = useAppSelector(getShowArchivedAssets);
    const gridLines = useAppSelector(getTableGridLines);
    const compactRows = useAppSelector(getTableCompactRows);
    const sortBy = useAppSelector(getAssetTableSortColumn);
    const sortDir = useAppSelector(getAssetTableSortDir);
    const assetTagLastSuccessfullyRemoved = useAppSelector(getAssetTagLastSuccessfullyRemoved);
    const assetTagLastFailedRemoved = useAppSelector(getAssetTagsLastFailedRemoved);
    const assetTagLastSuccessfullyAdded = useAppSelector(getAssetTagLastSuccessfullyAdded);
    const assetTagLastFailedAdded = useAppSelector(getAssetTagsLastFailedAdded);
    const updateSuccessful = useAppSelector(getAssetUpdateSuccessful);
    const updateFailed = useAppSelector(getAssetUpdateFailed);
    const updateErrorCode = useAppSelector(getAssetUpdateErrorCode);

    const intl = useIntl();

    // State hooks
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
    const [unsavedAssetChanges, setUnsavedAssetChanges] = useState(false);
    const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
    const [tagManagerSelectedTags, setTagManagerSelectedTags] = useState<SelectedTag[]>([]);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showAddToGroupDialog, setShowAddToGroupDialog] = useState(false);
    const [showBulkArchiveDialog, setShowBulkArchiveDialog] = useState(false);
    const [showRemoveFromGroupDialog, setShowRemoveFromGroupDialog] = useState(false);
    const [numberOfAssetsToDisplay, setNumberOfAssetsToDisplay] = useState(ASSETS_PER_PAGE);
    const [nextRowId, setNextRowId] = useState<string | null>(null);
    const [tableResetKey, setTableResetKey] = useState(0);
    const tableColumnsStorageKey = getAssetTableColumnsStorageKey(accountId);
    const [storedTableColumns, setStoredTableColumns, removeStoredTableColumns] = useLocalStorage<TableColumnDetails[]>(
        tableColumnsStorageKey,
        []
    );
    const previousTableColumnsStorageKey = useRef(tableColumnsStorageKey);
    const hasTableColumnsStorageKeyChanged = previousTableColumnsStorageKey.current !== tableColumnsStorageKey;
    const tableColumns = normalizeAssetTableColumns(hasTableColumnsStorageKeyChanged ? undefined : storedTableColumns);
    const displayableTableColumns = isDeviceSharingsFeatureEnabled()
        ? tableColumns
        : tableColumns.filter(column => column.key !== 'sharings');

    // Other hooks (useMemo, useCallback, useEffect)
    useEffect(() => {
        dispatch(fetchAssets());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchDevices());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchTags(accountId));
    }, [dispatch, accountId]);

    useEffect(() => {
        const nextSelectedAsset = assets.find(asset => asset.id === selectedAssetId) || null;
        setSelectedAsset(nextSelectedAsset);
    }, [setSelectedAsset, selectedAssetId, assets]);

    useEffect(() => {
        if (!hasTableColumnsStorageKeyChanged) {
            return;
        }

        const storedValue = window.localStorage.getItem(tableColumnsStorageKey);

        previousTableColumnsStorageKey.current = tableColumnsStorageKey;
        setStoredTableColumns(storedValue ? JSON.parse(storedValue) : []);
    }, [hasTableColumnsStorageKeyChanged, setStoredTableColumns, tableColumnsStorageKey]);

    useEffect(() => {
        const normalizedStoredColumns = normalizeAssetTableColumns(storedTableColumns);

        if (
            !hasTableColumnsStorageKeyChanged &&
            JSON.stringify(storedTableColumns) !== JSON.stringify(normalizedStoredColumns)
        ) {
            setStoredTableColumns(normalizedStoredColumns);
        }
    }, [hasTableColumnsStorageKeyChanged, storedTableColumns, setStoredTableColumns]);

    useEffect(() => {
        if (assetTagLastSuccessfullyRemoved) {
            const tagName = assetTagLastSuccessfullyRemoved.name;
            closableSuccessNotification(
                <FormattedMessage id={'intl-msg:asset.tag.remove.success'} values={{ tagName }} />,
                'success-notification-tag-removed'
            );
        }
    }, [assetTagLastSuccessfullyRemoved]);

    useEffect(() => {
        if (assetTagLastFailedRemoved) {
            const tagName = assetTagLastFailedRemoved.name;
            nonVanishingErrorNotification(
                <FormattedMessage id={'intl-msg:asset.tag.remove.failure'} values={{ tagName }} />,
                'T01'
            );
        }
    }, [assetTagLastFailedRemoved]);

    useEffect(() => {
        if (assetTagLastSuccessfullyAdded) {
            const tagName = assetTagLastSuccessfullyAdded.name;
            closableSuccessNotification(
                <FormattedMessage id={'intl-msg:asset.tag.add.success'} values={{ tagName }} />,
                'success-notification-tag-added'
            );
        }
    }, [assetTagLastSuccessfullyAdded]);

    useEffect(() => {
        if (assetTagLastFailedAdded) {
            const tagName = assetTagLastFailedAdded.name;
            nonVanishingErrorNotification(
                <FormattedMessage id={'intl-msg:asset.tag.add.failure'} values={{ tagName }} />,
                'T02'
            );
            dispatch(resetAssetTagsUpdatingState());
        }
    }, [dispatch, assetTagLastFailedAdded]);

    useEffect(() => {
        if (updateSuccessful) {
            closableSuccessNotification(<FormattedMessage id={'intl-msg:update.success'} />);
            dispatch(resetAssetUpdateState());
        } else if (updateFailed) {
            let errorString = '';
            if (updateErrorCode === 'A17') {
                errorString = 'intl-msg:update.error.A17';
            } else {
                errorString = `intl-msg:create.error${updateErrorCode ? `.${updateErrorCode}` : ''}`;
            }
            nonVanishingErrorNotification(
                <FormattedMessageWithFallback id={errorString} fallbackId={'intl-msg:update.failure'} />,
                updateErrorCode ?? 'AXX'
            );
            dispatch(resetAssetUpdateState());
        }
    }, [dispatch, updateSuccessful, updateFailed, updateErrorCode]);

    const handleActiveRowChange = (rowId: string | undefined) => {
        if (unsavedAssetChanges && rowId !== undefined) {
            if (rowId !== selectedAssetId) {
                setNextRowId(rowId);
            }
            setShowConfirmDialog(true);
        } else if (rowId && rowId !== selectedAssetId) {
            dispatch(setSelectedAssetId(rowId));
        } else {
            dispatch(setSelectedAssetId(null));
        }
    };

    const dispatchHandleSortChange = (column: string, sortDir: SortDirectionType) => {
        if (isAssetTableColumnSortable(column, tableColumns)) {
            dispatch(assetTableSortChanges({ sortBy: column, sortDir: sortDir }));
        }
    };

    const handleSortChange = (column: string) => {
        if (column === sortBy) {
            const newSortDir = sortDir === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
            dispatchHandleSortChange(column, newSortDir);
        } else {
            dispatchHandleSortChange(column, SortDirection.ASCENDING);
        }
    };

    const handleToggleAll = (shouldSelect: boolean) => {
        const allDisplayedRowIds = getFilteredAssets().map(asset => asset.id);

        if (shouldSelect) {
            setSelectedAssetIds(allDisplayedRowIds);
        } else {
            setSelectedAssetIds([]);
        }
    };

    const handleLoadMore = () => {
        setNumberOfAssetsToDisplay(numberOfAssetsToDisplay + ASSETS_PER_PAGE);
    };

    const handleColumnOrderChange = (columnOrder: string[]) => {
        setStoredTableColumns(currentColumns => {
            const normalizedColumns = normalizeAssetTableColumns(currentColumns);
            const orderedColumns = columnOrder
                .map(columnKey => normalizedColumns.find(column => column.key === columnKey))
                .filter((column): column is TableColumnDetails => column !== undefined);

            const missingColumns = normalizedColumns.filter(
                column => !orderedColumns.some(orderedColumn => orderedColumn.key === column.key)
            );

            return [...orderedColumns, ...missingColumns];
        });
    };

    const handleColumnVisibilityChange = (columnKey: string, visible: boolean) => {
        setStoredTableColumns(currentColumns =>
            normalizeAssetTableColumns(currentColumns).map(column =>
                column.key === columnKey ? { ...column, visible } : column
            )
        );
    };

    const handleColumnResize = (columnKey: string, width?: number | string) => {
        setStoredTableColumns(currentColumns =>
            normalizeAssetTableColumns(currentColumns).map(column => {
                if (column.key !== columnKey) {
                    return column;
                }

                if (width === undefined) {
                    const { width: _width, ...columnWithoutWidth } = column;

                    return columnWithoutWidth;
                }

                return { ...column, width };
            })
        );
    };

    const handleColumnsReset = () => {
        removeStoredTableColumns();
        setTableResetKey(currentValue => currentValue + 1);
    };

    const createNewTags = (tagsToUpdate: SelectedTag[]) => {
        const newTags = tagsToUpdate.filter(currentTag => currentTag.status === SelectedTagStatus.created);
        dispatch(createAssetTags(newTags));
    };

    const updateAssetTag = (asset: Asset, tagsToUpdate: SelectedTag[]) => {
        dispatch(updateAssetTags(asset, tagsToUpdate));
    };

    const assetNeedsTagUpdate = (selectedTags: SelectedTag[], asset: Asset) => {
        return selectedTags.some(assetTag => {
            if (assetTag.status === SelectedTagStatus.added) {
                return !asset.tags.includes(assetTag.id);
            } else if (assetTag.status === SelectedTagStatus.removed) {
                return asset.tags.includes(assetTag.id);
            } else {
                return true;
            }
        });
    };

    const createAndUpdateAssetTagsFromSelection = () => {
        createNewTags(tagManagerSelectedTags);
        const selectedTags = tagManagerSelectedTags;
        const selectedAssets = assets.filter(
            asset => selectedAssetIds.includes(asset.id) && assetNeedsTagUpdate(selectedTags, asset)
        );
        for (const asset of selectedAssets) {
            updateAssetTag(asset, tagManagerSelectedTags);
        }
    };

    const onClickAddToGroupDialogConfirm = () => {
        setShowAddToGroupDialog(false);
        createAndUpdateAssetTagsFromSelection();
    };

    const onClickRemoveFromGroupDialogConfirm = () => {
        setShowRemoveFromGroupDialog(false);
        createAndUpdateAssetTagsFromSelection();
    };

    const onSidebarChange = () => {
        setUnsavedAssetChanges(false);
        setShowConfirmDialog(false);
        setNextRowId(null);
        dispatch(setSelectedAssetId(nextRowId));
    };

    const getAddToGroupDialogAvailableTags = () => {
        const selectedTagsIntersection = intersection(
            ...assets.filter(asset => selectedAssetIds.includes(asset.id)).map(asset => asset.tags)
        );
        return availableTags.filter(tag => !selectedTagsIntersection.includes(tag.id));
    };

    const getRemoveFromGroupDialogAssetTags = () =>
        assets.filter(asset => selectedAssetIds.includes(asset.id)).flatMap(asset => asset.tags);

    const getRemoveFromGroupDialogAvailableTags = () =>
        availableTags.filter(tag => getRemoveFromGroupDialogAssetTags().includes(tag.id));

    const onTagListChange = (selectedTags: SelectedTag[]) => {
        setTagManagerSelectedTags(selectedTags.filter(tag => tag.status !== SelectedTagStatus.unchanged));
    };

    const getDevicesForAsset = (assetId: string): AssociatedDevice[] => {
        const devicesForAsset = devices[assetId];
        return devicesForAsset === undefined ? [] : devicesForAsset;
    };

    const getFormattedMessageAssetType = (assetType: AssetType) =>
        intl.formatMessage({ id: `intl-msg:asset.type.${assetType.toString()}` }).toLowerCase();

    const getFormattedMessageDeviceType = (deviceType: DeviceType) => deviceTypeToDisplayedString(deviceType);
    const visibleColumnKeys = displayableTableColumns.filter(column => column.visible).map(column => column.key);

    const getSearchableValuesForColumn = (asset: Asset, columnKey: string): string[] => {
        switch (columnKey) {
            case 'vehicle':
                return [
                    asset.name,
                    asset.identification?.trim() ? asset.identification : '',
                    getFormattedMessageAssetType(asset.type),
                ];
            case 'name':
                return [asset.name];
            case 'type':
                return [getFormattedMessageAssetType(asset.type)];
            case 'identification':
                return asset.identification?.trim() ? [asset.identification] : [];
            case 'license_plate':
                return [formatLicensePlateForDisplay(asset)];
            case 'brand':
                return [asset.brand ?? ''];
            case 'fuel_type':
                return [
                    asset.masterdata?.fuel_type
                        ? intl.formatMessage({ id: `intl-msg:masterdata.fuel-type.${asset.masterdata.fuel_type}` })
                        : '',
                ];
            case 'status':
                return [intl.formatMessage({ id: `intl-msg:asset.status.${asset.status}` })];
            case 'tags':
                return availableTags.filter(tag => asset.tags.includes(tag.id)).map(tag => tag.name);
            case 'devices':
                return getDevicesForAsset(asset.id).map(associatedDevice =>
                    getFormattedMessageDeviceType(associatedDevice.device.type)
                );
            default:
                return [];
        }
    };

    const filterBySearchValue = (assets: Asset[]): Asset[] => {
        const searchTerms = searchString.toLocaleLowerCase().split(' ').filter(Boolean);

        if (searchTerms.length === 0) {
            return assets;
        }

        return searchTerms.reduce(
            (prevResult, searchTerm) =>
                prevResult.filter(asset =>
                    visibleColumnKeys.some(columnKey =>
                        getSearchableValuesForColumn(asset, columnKey).some(value =>
                            value.toLocaleLowerCase().includes(searchTerm)
                        )
                    )
                ),
            assets
        );
    };

    const filteredByFilterOptions = (assets: Asset[]): Asset[] => {
        return assets
            .filter(asset => {
                const hasDataSource = getDevicesForAsset(asset.id).length > 0;

                if (hasDataSource) {
                    return showAssetsWithDataSource;
                }

                return showAssetsWithoutDataSource;
            })
            .filter(asset => (!showActiveAssets ? asset.status !== AssetStatus.active : true))
            .filter(asset => (!showArchivedAssets ? asset.status !== AssetStatus.archived : true));
    };

    const getFilteredAssets = () => {
        const assetsFilteredBySearchString = filterBySearchValue(assets);
        return filteredByFilterOptions(assetsFilteredBySearchString);
    };

    const getSortedAssets = (assets: Asset[]) => {
        if (sortBy) {
            if (sortBy === 'fuel_type') {
                return orderBy<Asset>(
                    assets,
                    [
                        asset =>
                            asset.masterdata?.fuel_type
                                ? intl.formatMessage({
                                      id: `intl-msg:masterdata.fuel-type.${asset.masterdata.fuel_type}`,
                                  })
                                : '',
                    ],
                    [sortDir]
                );
            }

            if (sortBy === 'type') {
                return orderBy<Asset>(assets, [asset => getFormattedMessageAssetType(asset.type)], [sortDir]);
            }

            const sortField = sortBy === 'vehicle' ? 'name' : sortBy;

            return orderBy<Asset>(assets, [sortField as Identifier<Asset>], [sortDir]);
        } else {
            return assets;
        }
    };

    const sortedAssets = getSortedAssets(getFilteredAssets());
    const assetsToRender = sortedAssets.slice(0, numberOfAssetsToDisplay);

    // Render helpers (optional)
    const renderTagManagerWrapper = (availableTags: Tag[], assetTags: string[] | undefined, useCustomTags: boolean) => (
        <TagManagerWrapper
            accountId={accountId}
            assetTags={assetTags}
            onTagListChange={onTagListChange}
            availableTags={availableTags}
            useCustomTags={useCustomTags}
            customMessage={
                useCustomTags
                    ? 'intl-msg:tag-manager.create-add-message'
                    : 'intl-msg:tag-manager.remove-message'
            }
        />
    );

    return (
        <>
            {showConfirmDialog && (
                <ConfirmationDialog
                    show={showConfirmDialog}
                    title={intl.formatMessage({ id: 'intl-msg:confirmation-dialog.title' })}
                    content={<FormattedMessage id={'intl-msg:confirmation-dialog.content'} />}
                    onClickConfirm={onSidebarChange}
                    onClickCancel={() => {
                        setNextRowId(null);
                        setShowConfirmDialog(false);
                    }}
                    cancelButtonText={<FormattedMessage id={'intl-msg:confirmation-dialog.button.cancel'} />}
                    confirmButtonText={<FormattedMessage id={'intl-msg:confirmation-dialog.button.confirm'} />}
                    disableConfirm={false}
                    useOverflow={false}
                    bsSize={'sm'}
                />
            )}
            {showAddToGroupDialog && (
                <ConfirmationDialog
                    show={showAddToGroupDialog}
                    title={intl.formatMessage({ id: 'intl-msg:addToGroup' })}
                    content={renderTagManagerWrapper(getAddToGroupDialogAvailableTags(), [], true)}
                    bsSize={'sm'}
                    onClickConfirm={onClickAddToGroupDialogConfirm}
                    onClickCancel={() => setShowAddToGroupDialog(false)}
                    cancelButtonText={<FormattedMessage id={'intl-msg:add-to-group-dialog.button.cancel'} />}
                    confirmButtonText={
                        <div data-cy={'confirmation-dialog-confirm-button'}>
                            <FormattedMessage id={'intl-msg:add-to-group-dialog.button.confirm'} />
                        </div>
                    }
                    useOverflow={false}
                />
            )}
            {showRemoveFromGroupDialog && (
                <ConfirmationDialog
                    show={showRemoveFromGroupDialog}
                    title={intl.formatMessage({ id: 'intl-msg:removeFromGroup' })}
                    content={renderTagManagerWrapper(
                        getRemoveFromGroupDialogAvailableTags(),
                        getRemoveFromGroupDialogAssetTags(),
                        false
                    )}
                    bsSize={'sm'}
                    onClickConfirm={onClickRemoveFromGroupDialogConfirm}
                    onClickCancel={() => setShowRemoveFromGroupDialog(false)}
                    cancelButtonText={<FormattedMessage id={'intl-msg:add-to-group-dialog.button.cancel'} />}
                    confirmButtonText={
                        <div data-cy={'confirmation-dialog-confirm-button'}>
                            <FormattedMessage id={'intl-msg:add-to-group-dialog.button.confirm'} />
                        </div>
                    }
                    useOverflow={false}
                />
            )}
            {showBulkArchiveDialog && (
                <BulkArchiveAssetConfirmationDialog
                    show={showBulkArchiveDialog}
                    assets={assets.filter(asset => selectedAssetIds.includes(asset.id))}
                    hideBulkArchiveAssetDialog={() => setShowBulkArchiveDialog(false)}
                />
            )}
            <ApplicationLayout.Sidebar className='right'>
                {selectedAsset && (
                    <AssetsListDetailContainer
                        asset={selectedAsset}
                        onClose={onSidebarChange}
                        toggleConfirmDialog={setShowConfirmDialog}
                        toggleUnsavedAssetChanges={setUnsavedAssetChanges}
                    />
                )}
            </ApplicationLayout.Sidebar>
            <ApplicationLayout.Body
                banner={
                    IS_FEATURE_BANNER_ENABLED && (
                        <ApplicationLayoutBodyBanner>
                            <ReleaseInfoBannerContent />
                        </ApplicationLayoutBodyBanner>
                    )
                }
            >
                {fetching ? (
                    <Spinner text={<FormattedMessage id={'intl-msg:spinner.loading.assets'} defaultMessage={'Loading vehicles...'} />} />
                ) : (
                    <>
                        <AssetsListToolbar
                            assets={sortedAssets}
                            columns={displayableTableColumns}
                            showCreateButton={assetsToRender.length > 0}
                            handleColumnOrderChange={handleColumnOrderChange}
                            handleColumnVisibilityChange={handleColumnVisibilityChange}
                            handleColumnsReset={handleColumnsReset}
                        />
                        <AssetsList
                            assets={assetsToRender}
                            columns={displayableTableColumns}
                            tableResetKey={tableResetKey}
                            devices={devices}
                            searchString={searchString}
                            onCreateAsset={() => dispatch(openEasyOnboardingDialog())}
                            selectedAsset={selectedAsset}
                            tableViewType={tableViewType}
                            handleActiveRowChange={handleActiveRowChange}
                            tags={availableTags}
                            handleSortChange={handleSortChange}
                            sortBy={sortBy}
                            sortDir={sortDir}
                            gridLines={gridLines}
                            compactRows={compactRows}
                            handleToggleAll={handleToggleAll}
                            handleColumnResize={handleColumnResize}
                            handleColumnOrderChange={handleColumnOrderChange}
                            handleColumnVisibilityChange={handleColumnVisibilityChange}
                            selectedAssetIds={selectedAssetIds}
                            handleSelectionChange={setSelectedAssetIds}
                            showAddToGroupDialog={setShowAddToGroupDialog}
                            showBulkArchiveDialog={setShowBulkArchiveDialog}
                            showRemoveFromGroupDialog={setShowRemoveFromGroupDialog}
                        />
                        {assetsToRender.length > 0 && (
                            <LoadMoreButton
                                data-test-id={'loadMoreButton'}
                                loaded={assetsToRender.length}
                                total={sortedAssets.length}
                                onLoadMore={handleLoadMore}
                                loadMoreMessage={<FormattedMessage id={'intl-msg:loadMore'} />}
                                noMoreMessage={<FormattedMessage id={'intl-msg:noMore'} />}
                            />
                        )}
                    </>
                )}
            </ApplicationLayout.Body>
        </>
    );
};
